/**
 * Sidebar behaviour: the icon rail toggle, pinned pages and the Ctrl+K command palette.
 *
 * Markup: resources/views/layouts/partials/_sidebar.blade.php and _command-palette.blade.php.
 * The page list (and who can see what) comes from App\Services\Navigation\NavigationRegistry; this
 * file only reads it from #commandPaletteIndex. Players, factions, contracts and bills come from the
 * search route (App\Services\Navigation\GlobalSearch) as the user types, listed after the pages.
 *
 * app.js loads in <head>, so everything waits for DOMContentLoaded.
 */

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content ?? '';

function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function savePreferences(sidebar, payload) {
    return fetch(sidebar.dataset.preferencesUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
    }).then((response) => {
        if (!response.ok) {
            throw new Error(`Saving sidebar preferences failed (${response.status})`);
        }

        return response.json();
    });
}

function readIndex() {
    try {
        return JSON.parse(document.getElementById('commandPaletteIndex')?.textContent ?? '[]');
    } catch (e) {
        return [];
    }
}

// ---- Icon rail ----

function initRail(sidebar) {
    const toggle = sidebar.querySelector('[data-sidebar-rail-toggle]');
    if (!toggle) {
        return;
    }

    const sync = () => {
        const rail = document.body.classList.contains('sidebar-rail');
        const label = rail ? 'Expand sidebar' : 'Collapse sidebar';
        toggle.title = label;
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('aria-pressed', rail ? 'true' : 'false');
    };

    sync();

    toggle.addEventListener('click', () => {
        const rail = document.body.classList.toggle('sidebar-rail');
        sync();
        // Otherwise focus keeps the rail open until the user clicks elsewhere.
        toggle.blur();
        savePreferences(sidebar, { collapsed: rail }).catch((error) => console.error(error));
    });
}

// ---- Pinned pages ----

function initPins(sidebar, index) {
    const pagesByKey = new Map(index.map((page) => [page.key, page]));
    const max = Number(sidebar.dataset.maxPinned || 5);
    const section = sidebar.querySelector('[data-sidebar-pinned]');
    const list = sidebar.querySelector('[data-sidebar-pinned-list]');
    let pinned = [];

    try {
        pinned = JSON.parse(sidebar.dataset.pinned || '[]');
    } catch (e) {
        pinned = [];
    }

    const pinButtonHtml = (page, isPinned) => `
        <button type="button" class="sidebar-pin ${isPinned ? 'is-pinned' : ''}" data-nav-pin="${escapeHtml(page.key)}"
                aria-pressed="${isPinned}" title="${isPinned ? 'Unpin' : 'Pin to top'}"
                aria-label="${isPinned ? 'Unpin' : 'Pin'} ${escapeHtml(page.label)}">
            <i class="${isPinned ? 'fas' : 'far'} fa-star" aria-hidden="true"></i>
        </button>`;

    const render = () => {
        list.innerHTML = pinned
            .map((key) => pagesByKey.get(key))
            .filter(Boolean)
            .map((page) => `
                <li class="sidebar-item">
                    <a href="${escapeHtml(page.url)}" class="nav-link text-white">
                        <i class="${escapeHtml(page.icon)} sidebar-icons" aria-hidden="true"></i>
                        <span class="sidebar-label">${escapeHtml(page.label)}</span>
                    </a>
                    ${pinButtonHtml(page, true)}
                </li>`)
            .join('');
        section.classList.toggle('d-none', pinned.length === 0);

        document.querySelectorAll('[data-nav-pin]').forEach((button) => {
            const isPinned = pinned.includes(button.dataset.navPin);
            const page = pagesByKey.get(button.dataset.navPin);
            button.classList.toggle('is-pinned', isPinned);
            button.setAttribute('aria-pressed', isPinned ? 'true' : 'false');
            button.title = isPinned ? 'Unpin' : 'Pin to top';
            if (page) {
                button.setAttribute('aria-label', `${isPinned ? 'Unpin' : 'Pin'} ${page.label}`);
            }
            const icon = button.querySelector('i');
            if (icon) {
                icon.className = `${isPinned ? 'fas' : 'far'} fa-star`;
            }
        });
    };

    const flash = (message) => {
        let note = section.parentElement.querySelector('.sidebar-pin-note');
        if (!note) {
            note = document.createElement('div');
            note.className = 'sidebar-pin-note small text-warning px-2 pb-2 sidebar-label';
            note.setAttribute('role', 'status');
            section.parentElement.insertBefore(note, section);
        }
        note.textContent = message;
        clearTimeout(note.hideTimer);
        note.hideTimer = setTimeout(() => note.remove(), 4000);
    };

    const toggle = (key) => {
        if (!pagesByKey.has(key)) {
            return;
        }

        const previous = [...pinned];

        if (pinned.includes(key)) {
            pinned = pinned.filter((k) => k !== key);
        } else if (pinned.length >= max) {
            flash(`You can pin up to ${max} pages. Unpin one first.`);
            return;
        } else {
            pinned = [...pinned, key];
        }

        render();
        savePreferences(sidebar, { pinned }).catch((error) => {
            console.error(error);
            pinned = previous;
            render();
            flash("Couldn't save your pins. Try again.");
        });
    };

    // Delegated, so it covers the pinned list (re-rendered) and the command palette's stars.
    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-nav-pin]');
        if (!button) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        toggle(button.dataset.navPin);
    });

    return {
        isPinned: (key) => pinned.includes(key),
        pinned: () => [...pinned],
    };
}

// ---- Command palette ----

/**
 * How well a page matches the query; 0 means no match. Every word in the query has to match the label,
 * group or keywords. A label that starts with the word scores highest, then a word inside the label,
 * then letters in order (so "wrc" finds "Wars & Chains").
 */
function score(page, words) {
    const label = page.label.toLowerCase();
    const extra = `${page.group ?? ''} ${page.keywords ?? ''}`.toLowerCase();
    let total = 0;

    for (const word of words) {
        if (label.startsWith(word)) {
            total += 100;
        } else if (new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(label)) {
            total += 80;
        } else if (label.includes(word)) {
            total += 60;
        } else if (extra.includes(word)) {
            total += 30;
        } else {
            const fuzzy = subsequence(label, word);
            if (fuzzy === 0) {
                return 0;
            }
            total += fuzzy;
        }
    }

    return total;
}

function subsequence(text, word) {
    let position = -1;
    let gaps = 0;

    for (const char of word) {
        const next = text.indexOf(char, position + 1);
        if (next === -1) {
            return 0;
        }
        if (position !== -1) {
            gaps += next - position - 1;
        }
        position = next;
    }

    return Math.max(5, 25 - gaps);
}

function highlight(label, words) {
    const lower = label.toLowerCase();
    const word = words.find((w) => lower.includes(w));
    if (!word) {
        return escapeHtml(label);
    }

    const at = lower.indexOf(word);
    return escapeHtml(label.slice(0, at))
        + `<mark>${escapeHtml(label.slice(at, at + word.length))}</mark>`
        + escapeHtml(label.slice(at + word.length));
}

function initPalette(index, pins) {
    const element = document.getElementById('commandPalette');
    if (!element || typeof bootstrap === 'undefined') {
        return;
    }

    const modal = bootstrap.Modal.getOrCreateInstance(element);
    const input = element.querySelector('[data-command-palette-input]');
    const results = element.querySelector('[data-command-palette-results]');
    const empty = element.querySelector('[data-command-palette-empty]');
    const searchUrl = element.dataset.searchUrl;
    let pageMatches = [];
    let records = [];
    let matches = [];
    let selected = 0;
    let searching = false;
    let recordTimer = null;
    let recordRequest = null;

    const wordsOf = (value) => value.toLowerCase().trim().split(/\s+/).filter(Boolean);

    // Matches GlobalSearch: a number of any length, or text of two characters or more.
    const searchesRecords = (query) => /^\d+$/.test(query) || query.length >= 2;

    const fetchRecords = (query) => {
        recordRequest?.abort();
        recordRequest = new AbortController();

        fetch(`${searchUrl}?q=${encodeURIComponent(query)}`, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            signal: recordRequest.signal,
        })
            .then((response) => (response.ok ? response.json() : { results: [] }))
            .then((data) => {
                if (input.value.trim() !== query) {
                    return;
                }
                records = (data.results ?? []).map((record) => ({ ...record, group: record.kind, record: true }));
                searching = false;
                matches = [...pageMatches, ...records];
                render(wordsOf(query));
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    searching = false;
                    render(wordsOf(query));
                }
            });
    };

    const search = () => {
        const query = input.value.trim();
        const words = wordsOf(query);

        if (words.length === 0) {
            // Pinned pages first, then everything in sidebar order.
            const pinned = pins ? pins.pinned() : [];
            pageMatches = [
                ...pinned.map((key) => index.find((page) => page.key === key)).filter(Boolean),
                ...index.filter((page) => !pinned.includes(page.key)),
            ];
        } else {
            pageMatches = index
                .map((page, order) => ({ page, order, score: score(page, words) }))
                .filter((m) => m.score > 0)
                .sort((a, b) => b.score - a.score || a.order - b.order)
                .map((m) => m.page);
        }

        // The old query's records go at once; the new query's arrive once the user pauses typing.
        clearTimeout(recordTimer);
        recordRequest?.abort();
        records = [];
        searching = Boolean(searchUrl) && searchesRecords(query);
        if (searching) {
            recordTimer = setTimeout(() => fetchRecords(query), 200);
        }

        matches = pageMatches;
        selected = 0;
        render(words);
    };

    const pageItem = (page, i, words) => {
        const isPinned = pins?.isPinned(page.key);
        return `
            <li role="option" id="command-palette-option-${i}" aria-selected="${i === selected}">
                <a href="${escapeHtml(page.url)}" class="command-palette__item ${i === selected ? 'is-selected' : ''}" data-index="${i}" tabindex="-1">
                    <i class="${escapeHtml(page.icon)}" aria-hidden="true"></i>
                    <span>${highlight(page.label, words)}</span>
                    ${page.group ? `<span class="command-palette__group">${escapeHtml(page.group)}</span>` : '<span class="command-palette__group"></span>'}
                    ${pins ? `<span class="sidebar-pin-inline" data-nav-pin="${escapeHtml(page.key)}" role="button" tabindex="-1"
                          title="${isPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}" aria-label="${isPinned ? 'Unpin' : 'Pin'} ${escapeHtml(page.label)}">
                          <i class="${isPinned ? 'fas' : 'far'} fa-star" aria-hidden="true"></i></span>` : ''}
                </a>
            </li>`;
    };

    const recordItem = (record, i, words) => `
        <li role="option" id="command-palette-option-${i}" aria-selected="${i === selected}">
            <a href="${escapeHtml(record.url)}" class="command-palette__item ${i === selected ? 'is-selected' : ''}" data-index="${i}" tabindex="-1"
               ${record.external ? 'target="_blank" rel="noopener"' : ''}>
                <i class="${escapeHtml(record.icon)}" aria-hidden="true"></i>
                <span class="command-palette__record">
                    <span>${highlight(record.label, words)}</span>
                    ${record.detail ? `<span class="command-palette__detail">${escapeHtml(record.detail)}</span>` : ''}
                </span>
                <span class="command-palette__group">${escapeHtml(record.kind)}${record.external
                    ? ' <i class="fas fa-arrow-up-right-from-square" title="Opens Torn in a new tab"></i>' : ''}</span>
            </a>
        </li>`;

    const render = (words = []) => {
        results.innerHTML = matches.map((match, i) => {
            const heading = match.record && !matches[i - 1]?.record
                ? '<li class="command-palette__heading" role="presentation">Records</li>'
                : '';
            return heading + (match.record ? recordItem(match, i, words) : pageItem(match, i, words));
        }).join('');

        empty.textContent = searching ? 'Searching...' : 'Nothing matches.';
        empty.classList.toggle('d-none', matches.length > 0);
        input.setAttribute('aria-activedescendant', matches.length ? `command-palette-option-${selected}` : '');
    };

    const move = (delta) => {
        if (matches.length === 0) {
            return;
        }
        selected = (selected + delta + matches.length) % matches.length;
        results.querySelectorAll('.command-palette__item').forEach((item, i) => {
            item.classList.toggle('is-selected', i === selected);
            item.parentElement.setAttribute('aria-selected', i === selected ? 'true' : 'false');
        });
        input.setAttribute('aria-activedescendant', `command-palette-option-${selected}`);
        results.querySelector('.is-selected')?.scrollIntoView({ block: 'nearest' });
    };

    const open = (page, newTab) => {
        if (!page) {
            return;
        }
        if (newTab || page.external) {
            window.open(page.url, '_blank', 'noopener');
            return;
        }
        window.location.href = page.url;
    };

    input.addEventListener('input', search);

    input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            open(matches[selected], event.ctrlKey || event.metaKey);
        }
    });

    results.addEventListener('mousemove', (event) => {
        const item = event.target.closest('.command-palette__item');
        if (item && Number(item.dataset.index) !== selected) {
            move(Number(item.dataset.index) - selected);
        }
    });

    // Re-draw after a pin toggled from inside the palette so its star flips.
    results.addEventListener('click', (event) => {
        if (event.target.closest('[data-nav-pin]')) {
            setTimeout(() => render(wordsOf(input.value)), 0);
        }
    }, true);

    element.addEventListener('show.bs.modal', () => {
        input.value = '';
        search();
    });

    element.addEventListener('shown.bs.modal', () => input.focus());

    document.querySelectorAll('[data-command-palette-open]').forEach((button) => {
        button.addEventListener('click', () => modal.show());
    });

    document.addEventListener('keydown', (event) => {
        if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            if (element.classList.contains('show')) {
                modal.hide();
            } else {
                modal.show();
            }
        }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const sidebar = document.getElementById('sidebarMenu');
    if (!sidebar) {
        return;
    }

    const index = readIndex();

    initRail(sidebar);
    const pins = initPins(sidebar, index);
    initPalette(index, pins);
});
